import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  Clock, 
  Send, 
  CheckCircle, 
  XCircle, 
  FileText, 
  User, 
  ArrowRight,
  MessageSquare,
  Paperclip,
  Building2,
  RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

// Status için Türkçe etiketler
const statusLabels = {
  'pending': 'Bekliyor',
  'approved': 'Onaylandı',
  'rejected': 'Reddedildi',
  'completed': 'Tamamlandı',
  'converted_to_job': 'İşe Dönüştürüldü',
  'forwarded_to_hr': 'İK\'ya İletildi',
  'forwarded_to_accounting': 'Muhasebe\'ye İletildi',
  'forwarded_to_purchasing': 'Satın Alma\'ya İletildi',
  'forwarded_to_admin': 'İdari İşler\'e İletildi',
  'forwarded_to_management': 'Yönetim\'e İletildi',
  'pending_hr': 'İK\'da Bekliyor',
  'pending_accounting': 'Muhasebe\'de Bekliyor',
  'pending_purchasing': 'Satın Alma\'da Bekliyor',
  'pending_admin': 'İdari İşler\'de Bekliyor',
  'pending_management': 'Yönetim\'de Bekliyor',
  'approved_by_management': 'Yönetim Onayladı',
  'rejected_by_branch': 'Şube Reddetti',
  'rejected_by_hr': 'İK Reddetti',
  'rejected_by_accounting': 'Muhasebe Reddetti',
  'rejected_by_purchasing': 'Satın Alma Reddetti',
  'rejected_by_management': 'Yönetim Reddetti',
  'correction': 'Düzeltme İstendi',
  'prepared': 'Hazırlandı',
  'created': 'Oluşturuldu'
};

// Status için icon ve renk
const getStatusConfig = (status) => {
  const configs = {
    'pending': { icon: Clock, color: 'bg-yellow-100 text-yellow-700', borderColor: 'border-yellow-400' },
    'approved': { icon: CheckCircle, color: 'bg-green-100 text-green-700', borderColor: 'border-green-400' },
    'completed': { icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700', borderColor: 'border-emerald-400' },
    'rejected': { icon: XCircle, color: 'bg-red-100 text-red-700', borderColor: 'border-red-400' },
    'converted_to_job': { icon: RefreshCw, color: 'bg-indigo-100 text-indigo-700', borderColor: 'border-indigo-400' },
    'forwarded_to_hr': { icon: Send, color: 'bg-blue-100 text-blue-700', borderColor: 'border-blue-400' },
    'forwarded_to_accounting': { icon: Send, color: 'bg-purple-100 text-purple-700', borderColor: 'border-purple-400' },
    'forwarded_to_purchasing': { icon: Send, color: 'bg-cyan-100 text-cyan-700', borderColor: 'border-cyan-400' },
    'forwarded_to_admin': { icon: Send, color: 'bg-orange-100 text-orange-700', borderColor: 'border-orange-400' },
    'forwarded_to_management': { icon: Send, color: 'bg-rose-100 text-rose-700', borderColor: 'border-rose-400' },
    'approved_by_management': { icon: CheckCircle, color: 'bg-green-100 text-green-700', borderColor: 'border-green-400' },
    'correction': { icon: RefreshCw, color: 'bg-amber-100 text-amber-700', borderColor: 'border-amber-400' },
    'prepared': { icon: FileText, color: 'bg-blue-100 text-blue-700', borderColor: 'border-blue-400' },
    'created': { icon: FileText, color: 'bg-gray-100 text-gray-700', borderColor: 'border-gray-400' }
  };
  
  // Default ve rejected variants
  if (status?.includes('rejected')) {
    return { icon: XCircle, color: 'bg-red-100 text-red-700', borderColor: 'border-red-400' };
  }
  if (status?.includes('pending')) {
    return { icon: Clock, color: 'bg-yellow-100 text-yellow-700', borderColor: 'border-yellow-400' };
  }
  if (status?.includes('forwarded')) {
    return { icon: Send, color: 'bg-blue-100 text-blue-700', borderColor: 'border-blue-400' };
  }
  
  return configs[status] || { icon: Clock, color: 'bg-gray-100 text-gray-700', borderColor: 'border-gray-400' };
};

// Tarih formatla
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateString;
  }
};

const RequestHistory = ({ request, className = '' }) => {
  if (!request) return null;
  
  // Tüm hareketleri topla
  const movements = [];
  
  // 1. Oluşturulma
  movements.push({
    type: 'created',
    status: 'created',
    timestamp: request.created_at,
    user_name: request.owner_name || request.created_by_name || request.requester_name || 'Bilinmiyor',
    note: request.description || request.aciklama || 'Talep oluşturuldu',
    documents: request.documents || request.belgeler || [],
    branch_name: request.branch_name || request.sube_adi
  });
  
  // 2. Status history'den hareketler
  if (request.status_history && Array.isArray(request.status_history)) {
    request.status_history.forEach(entry => {
      movements.push({
        type: 'status_change',
        status: entry.status,
        timestamp: entry.timestamp || entry.date || entry.created_at,
        user_name: entry.user_name || entry.changed_by_name || entry.by_name || 'Sistem',
        user_id: entry.user_id || entry.changed_by,
        note: entry.note || entry.comment || entry.message || '',
        documents: entry.documents || entry.belgeler || [],
        target_department: entry.target_department || entry.department,
        from_status: entry.from_status,
        to_status: entry.to_status || entry.status
      });
    });
  }
  
  // 3. Yönlendirme geçmişi
  if (request.forwarding_history && Array.isArray(request.forwarding_history)) {
    request.forwarding_history.forEach(entry => {
      movements.push({
        type: 'forwarded',
        status: `forwarded_to_${entry.to_department || entry.target}`,
        timestamp: entry.timestamp || entry.forwarded_at || entry.date,
        user_name: entry.forwarded_by_name || entry.user_name || 'Sistem',
        note: entry.note || entry.comment || `${entry.from_department || ''} → ${entry.to_department || entry.target || ''}`,
        documents: entry.documents || [],
        from_department: entry.from_department,
        to_department: entry.to_department || entry.target
      });
    });
  }
  
  // 4. Onay/Red bilgileri
  if (request.approved_at || request.approval_date) {
    movements.push({
      type: 'approved',
      status: 'approved',
      timestamp: request.approved_at || request.approval_date,
      user_name: request.approved_by_name || request.approver_name || 'Yönetici',
      note: request.approval_note || request.onay_notu || 'Onaylandı'
    });
  }
  
  if (request.rejected_at || request.rejection_date) {
    movements.push({
      type: 'rejected',
      status: request.status || 'rejected',
      timestamp: request.rejected_at || request.rejection_date,
      user_name: request.rejected_by_name || request.rejector_name || 'Yönetici',
      note: request.rejection_reason || request.red_nedeni || 'Reddedildi'
    });
  }
  
  // 5. Tamamlanma bilgisi
  if (request.completed_at || request.completion_date) {
    movements.push({
      type: 'completed',
      status: 'completed',
      timestamp: request.completed_at || request.completion_date,
      user_name: request.completed_by_name || 'Sistem',
      note: request.completion_note || 'Tamamlandı'
    });
  }
  
  // 6. İşe dönüştürülme
  if (request.converted_to_job_at) {
    movements.push({
      type: 'converted',
      status: 'converted_to_job',
      timestamp: request.converted_to_job_at,
      user_name: request.converted_by_name || 'Satın Alma',
      note: request.job_id ? `İş No: ${request.job_id}` : 'İşe dönüştürüldü'
    });
  }
  
  // Tarihe göre sırala (en yeni en üstte)
  movements.sort((a, b) => {
    const dateA = new Date(a.timestamp || 0);
    const dateB = new Date(b.timestamp || 0);
    return dateB - dateA;
  });
  
  // Duplicate'ları kaldır (aynı timestamp ve status olanlar)
  const uniqueMovements = movements.filter((movement, index, self) => {
    return index === self.findIndex(m => 
      m.timestamp === movement.timestamp && 
      m.status === movement.status &&
      m.user_name === movement.user_name
    );
  });

  return (
    <Card className={`mt-4 ${className}`} data-testid="request-history">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-5 w-5 text-purple-600" />
          Talep Hareketi
          <Badge variant="secondary" className="ml-2 bg-purple-100 text-purple-700">
            {uniqueMovements.length} hareket
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[300px] pr-4">
          <div className="relative">
            {/* Timeline çizgisi */}
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
            
            <div className="space-y-4">
              {uniqueMovements.map((movement, index) => {
                const config = getStatusConfig(movement.status);
                const Icon = config.icon;
                const statusLabel = statusLabels[movement.status] || movement.status || 'Güncelleme';
                
                return (
                  <div key={index} className="relative pl-10">
                    {/* Timeline noktası */}
                    <div className={`absolute left-2 w-5 h-5 rounded-full border-2 ${config.borderColor} bg-white flex items-center justify-center`}>
                      <Icon className={`h-3 w-3 ${config.color.split(' ')[1]}`} />
                    </div>
                    
                    <div className={`p-3 rounded-lg border ${config.borderColor} bg-white shadow-sm`}>
                      {/* Üst satır: Status ve tarih */}
                      <div className="flex items-center justify-between mb-2">
                        <Badge className={config.color}>
                          {statusLabel}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {formatDate(movement.timestamp)}
                        </span>
                      </div>
                      
                      {/* Kullanıcı bilgisi */}
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <User className="h-3.5 w-3.5" />
                        <span className="font-medium">{movement.user_name}</span>
                        {movement.branch_name && (
                          <>
                            <Building2 className="h-3.5 w-3.5 ml-2" />
                            <span>{movement.branch_name}</span>
                          </>
                        )}
                      </div>
                      
                      {/* Yönlendirme bilgisi */}
                      {(movement.from_department || movement.to_department) && (
                        <div className="flex items-center gap-2 text-sm text-blue-600 mb-1">
                          <ArrowRight className="h-3.5 w-3.5" />
                          <span>
                            {movement.from_department && `${movement.from_department} → `}
                            {movement.to_department}
                          </span>
                        </div>
                      )}
                      
                      {/* Not/Açıklama */}
                      {movement.note && (
                        <div className="flex items-start gap-2 text-sm text-gray-700 mt-2 bg-gray-50 p-2 rounded">
                          <MessageSquare className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                          <span className="whitespace-pre-wrap">{movement.note}</span>
                        </div>
                      )}
                      
                      {/* Belgeler */}
                      {movement.documents && movement.documents.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                            <Paperclip className="h-3 w-3" />
                            <span>Eklenen Belgeler:</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {movement.documents.map((doc, docIndex) => {
                              const fileName = typeof doc === 'string' ? doc : (doc.name || doc.filename || doc.file_name || 'Belge');
                              const fileUrl = typeof doc === 'string' ? doc : (doc.url || doc.path || doc.file_path);
                              
                              return (
                                <a
                                  key={docIndex}
                                  href={fileUrl?.startsWith('http') ? fileUrl : `${API}/${fileUrl}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                                >
                                  <FileText className="h-3 w-3" />
                                  <span className="max-w-[150px] truncate">{fileName}</span>
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {uniqueMovements.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Henüz hareket kaydı yok</p>
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default RequestHistory;
